import { useEffect, useRef, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation } from 'react-router';
import { useAuth } from '../auth/AuthProvider';
import { LOCALES, LOCALE_NAMES, useI18n, type Locale } from '../i18n';
import { useMe } from '../hooks/useCollaboration';
import { useUnreadCount } from '../hooks/usePhase2';
import { useTenantBranding } from '../hooks/usePhase4';
import { useAdoptProfilePrefs, useUiPrefs } from '../hooks/useUiPrefs';
import type { ThemeMode } from '../prefs';
import type { Role } from '../types/api';
import { humanize } from '../utils/format';
import { Icon, type IconName } from './Icon';

export function RequireAuth() {
  const { status } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  if (status === 'restoring') {
    return (
      <p className="page muted" role="status">
        {t('Restoring session…')}
      </p>
    );
  }
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return <AppShell />;
}

// Defined here rather than imported from the admin chunk, so the shell never pulls admin code.
const ADMIN_ROLES: readonly Role[] = ['system_admin', 'customer_admin', 'team_lead'];
const THEME_ORDER: readonly ThemeMode[] = ['system', 'light', 'dark'];
const THEME_ICON: Record<ThemeMode, IconName> = { system: 'system', light: 'sun', dark: 'moon' };
const THEME_LABEL: Record<ThemeMode, string> = { system: 'System', light: 'Light', dark: 'Dark' };

function NavItem({ to, icon, label, end, badge }: { to: string; icon: IconName; label: string; end?: boolean; badge?: string | undefined }) {
  const { t } = useI18n();
  return (
    <li>
      <NavLink to={to} end={end ?? false} className="nav-link" title={label} aria-label={badge ? t('{label}, {n} unread', { label, n: badge }) : undefined}>
        <span className="nav-icon">
          <Icon name={icon} />
          {badge && <span className="nav-badge" aria-hidden="true">{badge}</span>}
        </span>
        <span className="nav-label">{label}</span>
      </NavLink>
    </li>
  );
}

/** Theme and language quick switches (also used on the sign-in page). */
export function QuickPrefs({ compact = false }: { compact?: boolean }) {
  const { t } = useI18n();
  const [prefs, save] = useUiPrefs();
  const next = THEME_ORDER[(THEME_ORDER.indexOf(prefs.theme) + 1) % THEME_ORDER.length] ?? 'system';
  const nextLocale: Locale = prefs.locale === 'en' ? 'es' : 'en';
  return (
    <div className={`quick-prefs ${compact ? 'compact' : ''}`}>
      <button
        type="button"
        className="nav-link as-button"
        onClick={() => save({ theme: next })}
        title={t('Theme: {mode}. Switch to {next}.', { mode: t(THEME_LABEL[prefs.theme]), next: t(THEME_LABEL[next]) })}
        aria-label={t('Theme: {mode}. Switch to {next}.', { mode: t(THEME_LABEL[prefs.theme]), next: t(THEME_LABEL[next]) })}
      >
        <span className="nav-icon">
          <Icon name={THEME_ICON[prefs.theme]} />
        </span>
        <span className="nav-label">{t(THEME_LABEL[prefs.theme])}</span>
      </button>
      <button
        type="button"
        className="nav-link as-button"
        lang={nextLocale}
        onClick={() => save({ locale: nextLocale })}
        title={LOCALE_NAMES[nextLocale]}
        aria-label={`${t('Language')}: ${LOCALE_NAMES[prefs.locale]}. ${LOCALE_NAMES[nextLocale]}`}
      >
        <span className="nav-icon">
          <Icon name="language" />
        </span>
        <span className="nav-label">
          {LOCALE_NAMES[prefs.locale]} <span className="locale-code">{LOCALES.map((l) => l.toUpperCase()).join(' / ')}</span>
        </span>
      </button>
    </div>
  );
}

function AppShell() {
  const { claims, logout } = useAuth();
  const { t } = useI18n();
  const me = useMe(claims !== null);
  const branding = useTenantBranding(claims !== null);
  const unread = useUnreadCount(true);
  const [prefs, save] = useUiPrefs();
  useAdoptProfilePrefs();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();
  const menuButton = useRef<HTMLButtonElement>(null);

  // Close the mobile drawer on navigation and on Escape (returning focus to the menu button).
  useEffect(() => setDrawerOpen(false), [location.pathname]);
  useEffect(() => {
    if (!drawerOpen) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setDrawerOpen(false);
        menuButton.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [drawerOpen]);

  const count = unread.data?.count ?? 0;
  const badge = count > 0 ? (count >= 100 ? '99+' : String(count)) : undefined;
  const productName = branding.data?.productName ?? 'ResolveOne';
  const initials = (me.data?.name ?? '?').split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();

  return (
    <div className="shell" data-collapsed={prefs.sidebarCollapsed ? 'true' : 'false'} data-drawer={drawerOpen ? 'open' : 'closed'}>
      <a href="#content" className="skip-link">
        {t('Skip to content')}
      </a>

      <header className="mobile-bar">
        <button ref={menuButton} type="button" className="icon-button" aria-label={t('Open menu')} aria-expanded={drawerOpen} aria-controls="sidebar" onClick={() => setDrawerOpen(true)}>
          <Icon name="menu" />
        </button>
        <NavLink to="/dashboard" className="brand">
          {branding.data?.logoUrl && <img src={branding.data.logoUrl} alt="" className="brand-logo" />}
          {productName}
        </NavLink>
        <NavLink to="/notifications" className="icon-button" aria-label={badge ? t('{label}, {n} unread', { label: t('Notifications'), n: badge }) : t('Notifications')}>
          <span className="nav-icon">
            <Icon name="bell" />
            {badge && <span className="nav-badge" aria-hidden="true">{badge}</span>}
          </span>
        </NavLink>
      </header>

      <div className="backdrop" hidden={!drawerOpen} onClick={() => setDrawerOpen(false)} />

      <aside id="sidebar" className="sidebar" aria-label={t('Main navigation')}>
        <div className="sidebar-head">
          <NavLink to="/dashboard" className="brand" title={productName}>
            {branding.data?.logoUrl ? <img src={branding.data.logoUrl} alt="" className="brand-logo" /> : <span className="brand-mark" aria-hidden="true">{productName[0]}</span>}
            <span className="nav-label">{productName}</span>
          </NavLink>
          <button type="button" className="icon-button drawer-close" aria-label={t('Close menu')} onClick={() => setDrawerOpen(false)}>
            <Icon name="close" />
          </button>
        </div>

        <NavLink to="/tickets/new" className="btn btn-primary new-ticket" title={t('New ticket')}>
          <Icon name="plus" />
          <span className="nav-label">{t('New ticket')}</span>
        </NavLink>

        <nav aria-label={t('Primary')}>
          <ul className="nav-list">
            <NavItem to="/dashboard" icon="dashboard" label={t('Dashboard')} />
            <NavItem to="/tickets" end icon="tickets" label={t('Tickets')} />
            <NavItem to="/reports" icon="reports" label={t('Reports')} />
            <NavItem to="/notifications" icon="bell" label={t('Notifications')} badge={badge} />
            {claims && ADMIN_ROLES.includes(claims.role) && <NavItem to="/admin" icon="admin" label={t('Administration')} />}
            <NavItem to="/settings" icon="settings" label={t('Settings')} />
          </ul>
        </nav>

        <div className="sidebar-foot">
          <QuickPrefs />
          <div className="user-card">
            <span className="avatar" aria-hidden="true">{initials}</span>
            <span className="nav-label user-meta">
              <span className="user-name">{me.data?.name ?? ''}</span>
              <span className="muted">{me.data?.customRole?.name ?? (me.data ? humanize(me.data.role) : '')}</span>
            </span>
          </div>
          <button type="button" className="nav-link as-button" onClick={() => void logout()} title={t('Sign out')}>
            <span className="nav-icon">
              <Icon name="logout" />
            </span>
            <span className="nav-label">{t('Sign out')}</span>
          </button>
          <button
            type="button"
            className="nav-link as-button collapse-toggle"
            aria-pressed={prefs.sidebarCollapsed}
            title={prefs.sidebarCollapsed ? t('Expand menu') : t('Collapse menu')}
            onClick={() => save({ sidebarCollapsed: !prefs.sidebarCollapsed })}
          >
            <span className="nav-icon">
              <Icon name={prefs.sidebarCollapsed ? 'expand' : 'collapse'} />
            </span>
            <span className="nav-label">{t('Collapse menu')}</span>
          </button>
        </div>
      </aside>

      <div id="content" className="content">
        <Outlet />
      </div>
    </div>
  );
}
